/**
 * ExpenseCategoryMappings — admin UI for the Category → GL Account map.
 *
 * Backend contract (P15-EXP1):
 *   GET    /api/expense-category-mappings
 *   POST   /api/expense-category-mappings   {category, gl_account_id, gl_account_name}
 *   PUT    /api/expense-category-mappings/{id}
 *   DELETE /api/expense-category-mappings/{id}
 *
 * The Expense Report Engine resolves these mappings at approval time to
 * build multi-debit journals. Categories without a mapping fall back to
 * the report's debit_account and surface in ``unmapped_categories`` on
 * the resulting journal.
 *
 * Validation rule (UI-level): only ``account_type='expenses'`` accounts
 * may be selected as GL targets. Bank / Cash / AR / AP / Equity accounts
 * are filtered out — the dropdown literally cannot show them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Search, AlertTriangle, X } from 'lucide-react';

export default function ExpenseCategoryMappings({ user }) {
  const [mappings, setMappings]   = useState([]);
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  const [draft, setDraft]         = useState({
    category: '', gl_account_id: '', gl_account_name: '',
    admin_gl_account_id: '', admin_gl_account_name: '',
  });
  const [showAdd, setShowAdd]     = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const isAdmin = user?.role === 'admin' || user?.role === 'accountant';

  // ── Bootstrap ────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [m, a] = await Promise.all([
          axiosInstance.get('/expense-category-mappings'),
          axiosInstance.get('/accounts'),
        ]);
        setMappings(m.data || []);
        setAccounts(a.data || []);
      } catch (_) {
        toast.error('Failed to load category mappings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Only Expense-type accounts are eligible. Bank / Cash / AR / AP /
  // Equity / Revenue are deliberately excluded — they would corrupt
  // the P&L if used as an expense debit target.
  const expenseAccounts = useMemo(() =>
    accounts
      .filter(a => (a.account_type || '').toLowerCase() === 'expenses')
      .filter(a => (a.status || 'active') !== 'inactive')
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || '')),
    [accounts],
  );

  const filteredMappings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mappings;
    return mappings.filter(m =>
      (m.category || '').toLowerCase().includes(q) ||
      (m.gl_account_name || '').toLowerCase().includes(q),
    );
  }, [mappings, search]);

  // ── Create ───────────────────────────────────────────────────────
  const submitNew = async () => {
    if (!draft.category.trim()) { toast.error('Category is required'); return; }
    if (!draft.gl_account_id)   { toast.error('Project GL account is required'); return; }
    setSaving(true);
    try {
      const projectAcc = expenseAccounts.find(a => a.id === draft.gl_account_id);
      const adminAcc   = draft.admin_gl_account_id
        ? expenseAccounts.find(a => a.id === draft.admin_gl_account_id)
        : null;
      const { data } = await axiosInstance.post('/expense-category-mappings', {
        category:              draft.category.trim(),
        gl_account_id:         draft.gl_account_id,
        gl_account_name:       projectAcc?.account_name || draft.gl_account_name,
        admin_gl_account_id:   draft.admin_gl_account_id || null,
        admin_gl_account_name: adminAcc?.account_name || null,
      });
      setMappings(ms => [...ms, data].sort((a, b) => a.category.localeCompare(b.category)));
      setDraft({ category: '', gl_account_id: '', gl_account_name: '', admin_gl_account_id: '', admin_gl_account_name: '' });
      setShowAdd(false);
      toast.success('Mapping created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create mapping');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────
  const beginEdit = (m) => {
    setEditingId(m.id);
    setEditDraft({
      category:            m.category,
      gl_account_id:       m.gl_account_id,
      admin_gl_account_id: m.admin_gl_account_id || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const projectAcc = expenseAccounts.find(a => a.id === editDraft.gl_account_id);
      const adminAcc   = editDraft.admin_gl_account_id
        ? expenseAccounts.find(a => a.id === editDraft.admin_gl_account_id)
        : null;
      const { data } = await axiosInstance.put(`/expense-category-mappings/${id}`, {
        category:              editDraft.category?.trim(),
        gl_account_id:         editDraft.gl_account_id,
        gl_account_name:       projectAcc?.account_name,
        admin_gl_account_id:   editDraft.admin_gl_account_id || '',
        admin_gl_account_name: adminAcc?.account_name || '',
      });
      setMappings(ms => ms.map(m => m.id === id ? data : m));
      cancelEdit();
      toast.success('Mapping updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────
  const remove = async (id, label) => {
    if (!window.confirm(`Delete mapping for "${label}"?`)) return;
    try {
      await axiosInstance.delete(`/expense-category-mappings/${id}`);
      setMappings(ms => ms.filter(m => m.id !== id));
      toast.success('Mapping deleted');
    } catch (_) {
      toast.error('Failed to delete');
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <ShieldHint />
        <p className="mt-4 text-gray-500">Admin or Accountant access required.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="expense-category-mappings-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Expense Category Mappings</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Map each expense category (e.g. <span className="font-mono">Fuel</span>, <span className="font-mono">Hotel</span>) to the GL
            account it should debit when an Expense Report is approved. Without a mapping, the
            category falls back to the report&apos;s default account.
          </p>
        </div>
        <button
          data-testid="add-mapping-btn"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-md transition"
        >
          <Plus size={16} /> Add Mapping
        </button>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          data-testid="mapping-search"
          placeholder="Search category or account…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
        />
      </div>

      {/* Add form */}
      {showAdd && (
        <div
          data-testid="add-mapping-form"
          className="mb-4 border border-emerald-200 bg-emerald-50/40 rounded-lg p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
        >
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <input
              data-testid="new-mapping-category"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              placeholder="e.g. Fuel"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Project GL Account <span className="text-rose-600">*</span>
              <span className="text-gray-400 font-normal ml-1">(51xxx · used when expense has a project)</span>
            </label>
            <select
              data-testid="new-mapping-account"
              value={draft.gl_account_id}
              onChange={(e) => setDraft({ ...draft, gl_account_id: e.target.value })}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
            >
              <option value="">— Select project debit account —</option>
              {expenseAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.account_number} · {a.account_name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Admin GL Account <span className="text-gray-400 font-normal">(optional · 52xxx · used when NO project)</span>
            </label>
            <select
              data-testid="new-mapping-admin-account"
              value={draft.admin_gl_account_id}
              onChange={(e) => setDraft({ ...draft, admin_gl_account_id: e.target.value })}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
            >
              <option value="">— Falls back to company default —</option>
              {expenseAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.account_number} · {a.account_name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 flex gap-2">
            <button
              data-testid="save-mapping-btn"
              onClick={submitNew}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3 py-2 rounded-md disabled:opacity-50"
            >
              <Save size={16} />
            </button>
            <button
              onClick={() => { setShowAdd(false); setDraft({ category: '', gl_account_id: '', gl_account_name: '', admin_gl_account_id: '', admin_gl_account_name: '' }); }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
      ) : filteredMappings.length === 0 ? (
        <EmptyState onAdd={() => setShowAdd(true)} hasFilter={!!search.trim()} />
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="mappings-table">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Project GL <span className="text-gray-400 normal-case font-normal">(51xxx)</span></th>
                <th className="px-4 py-3">Admin GL <span className="text-gray-400 normal-case font-normal">(52xxx · optional)</span></th>
                <th className="px-4 py-3 w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMappings.map(m => (
                <tr key={m.id} className="hover:bg-gray-50" data-testid={`mapping-row-${m.id}`}>
                  <td className="px-4 py-3">
                    {editingId === m.id ? (
                      <input
                        value={editDraft.category || ''}
                        onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })}
                        className="w-full border border-gray-200 rounded px-2 py-1 text-sm"
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{m.category}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editingId === m.id ? (
                      <select
                        value={editDraft.gl_account_id || ''}
                        onChange={(e) => setEditDraft({ ...editDraft, gl_account_id: e.target.value })}
                        className="w-full border border-gray-200 rounded px-2 py-1 text-sm bg-white"
                      >
                        {expenseAccounts.map(a => (
                          <option key={a.id} value={a.id}>{a.account_number} · {a.account_name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-700">
                        <span className="text-xs font-mono text-gray-400 mr-2">
                          {accounts.find(a => a.id === m.gl_account_id)?.account_number || ''}
                        </span>
                        {m.gl_account_name}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editingId === m.id ? (
                      <select
                        data-testid={`edit-admin-${m.id}`}
                        value={editDraft.admin_gl_account_id || ''}
                        onChange={(e) => setEditDraft({ ...editDraft, admin_gl_account_id: e.target.value })}
                        className="w-full border border-gray-200 rounded px-2 py-1 text-sm bg-white"
                      >
                        <option value="">— Company default —</option>
                        {expenseAccounts.map(a => (
                          <option key={a.id} value={a.id}>{a.account_number} · {a.account_name}</option>
                        ))}
                      </select>
                    ) : m.admin_gl_account_id ? (
                      <span className="text-gray-700">
                        <span className="text-xs font-mono text-gray-400 mr-2">
                          {accounts.find(a => a.id === m.admin_gl_account_id)?.account_number || ''}
                        </span>
                        {m.admin_gl_account_name}
                      </span>
                    ) : (
                      <span className="text-xs italic text-gray-400">Falls back to company default</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingId === m.id ? (
                      <div className="inline-flex gap-2">
                        <button
                          data-testid={`save-edit-${m.id}`}
                          onClick={() => saveEdit(m.id)}
                          disabled={saving}
                          className="text-emerald-600 hover:text-emerald-800 text-xs font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-500 hover:text-gray-800 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-3">
                        <button
                          data-testid={`edit-${m.id}`}
                          onClick={() => beginEdit(m)}
                          className="text-gray-600 hover:text-emerald-700 text-xs font-medium"
                        >
                          Edit
                        </button>
                        <button
                          data-testid={`delete-${m.id}`}
                          onClick={() => remove(m.id, m.category)}
                          className="text-rose-600 hover:text-rose-800"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer note */}
      <div className="mt-6 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          Only <span className="font-mono">account_type=expenses</span> accounts are selectable.
          Categories without a mapping fall back to each report&apos;s default debit account and are
          flagged on the resulting journal under <span className="font-mono">unmapped_categories</span>.
        </span>
      </div>
    </div>
  );
}

function EmptyState({ onAdd, hasFilter }) {
  return (
    <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
      <div className="text-gray-500 text-sm">
        {hasFilter ? 'No mappings match your search.' : 'No category mappings yet.'}
      </div>
      {!hasFilter && (
        <button
          data-testid="empty-state-add"
          onClick={onAdd}
          className="mt-3 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-md transition"
        >
          <Plus size={16} /> Add your first mapping
        </button>
      )}
    </div>
  );
}

function ShieldHint() {
  return <AlertTriangle size={48} className="mx-auto text-amber-500" />;
}
